import {
    DownOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
    Flex, TableColumnsType, Tag,
    Typography,
} from 'antd';
import { useMemo } from 'react';

import { dashboardQueries } from '@/api';
import { DefaultTable } from '@/components';
import { ChangelogRelease } from '@/types';
import { formatDate } from '@/utils';

type ChangelogTableProps = {
    title?: string | null;
};

const getTagColor = (type: string): string => {
    const normalizedType = type.toLowerCase();
    switch (normalizedType) {
        case 'added':
            return 'green';
        case 'changed':
            return 'blue';
        case 'fixed':
            return 'orange';
        case 'deprecated':
            return 'purple';
        case 'removed':
            return 'red';
        case 'security':
            return 'magenta';
        default:
            return 'default';
    }
};

export const ChangelogTable = ({
    title,
}: ChangelogTableProps) => {
    const { data, isFetching } = useQuery(dashboardQueries.getChangelog());
    const columns: TableColumnsType<ChangelogRelease> = useMemo(() => [
        {
            title: 'Версія',
            dataIndex: 'version',
            key: 'version',
            width: 200,
            render: (version: string) => (
                <Typography.Text strong>{version}</Typography.Text>
            ),
        },
        {
            title: 'Дата',
            dataIndex: 'date',
            key: 'date',
            width: 160,
            render: (date: string) => formatDate(date),
        },
        {
            title: 'Зміни',
            dataIndex: 'sections',
            key: 'sections',
            width: 300,
            render: (sections: ChangelogRelease['sections']) => {
                if (!sections || sections.length === 0) {
                    return (
                        <Typography.Text type="secondary">
                            Немає змін
                        </Typography.Text>
                    );
                }

                return (
                    <Flex wrap="wrap" gap={8}>
                        {sections.map((section, index) => (
                            <Tag key={index} color={getTagColor(section.type)}>
                                {section.type} ({section.items.length})
                            </Tag>
                        ))}
                    </Flex>
                );
            },
        },
    ], []);

    const expandable = useMemo(() => ({
        expandIcon: ({ expanded, onExpand, record }: any) => (
            expanded ? (
                <DownOutlined onClick={(evt) => onExpand(record, evt)} />
            ) : (
                <RightOutlined onClick={(evt) => onExpand(record, evt)} />
            )
        ),
        expandedRowRender: (record: ChangelogRelease) => {
            if (!record.sections || record.sections.length === 0) {
                return (
                    <Typography.Text type="secondary">
                        Немає змін
                    </Typography.Text>
                );
            }

            return (
                <div>
                    {record.sections.map((section, sectionIndex) => (
                        <div key={sectionIndex} style={{ marginBottom: 16 }}>
                            <Typography.Title level={5}>
                                {section.type}
                            </Typography.Title>
                            <ul>
                                {section.items.map((item, itemIndex) => (
                                    <li key={itemIndex}>
                                        <Typography.Text>{item}</Typography.Text>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            );
        },
    }), []);

    return (
        <DefaultTable
            title={title}
            pagination={false}
            columns={columns}
            dataSource={data?.items || []}
            total={data?.total || 0}
            loading={isFetching}
            expandable={expandable}
            hasSearch={false}
            rowKey="version"
        />
    );
};
